import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../ui/Card";
import Stat from "../ui/Stat";
import Table from "../ui/Table";
import { fetchPilotOperationsState, generatePilotReportNow, resendPilotReportEmail } from "../utils/pilotOperations";

function formatDateTime(value) {
  const ts = Number(value || 0);
  if (!ts) return "Not generated yet";
  return new Date(ts).toLocaleString([], {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function toIsoString(value) {
  const ts = Number(value || 0);
  if (!ts) return "";
  return new Date(ts).toISOString();
}

function toCsvValue(value) {
  const text = value == null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadFile(filename, content, contentType) {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function formatEmailStatus(report = {}) {
  const status = report.emailStatus || report.delivery?.status || "Pending";
  if (status === "accepted") {
    return "Accepted by relay; provider delivery unconfirmed.";
  }
  if (status === "sent") {
    return "Confirmed provider handoff.";
  }
  if (status === "unknown") {
    return "Email attempted; delivery unconfirmed. Use download links to archive manually.";
  }
  if (report.emailLastError) return `${status}: ${report.emailLastError}`;
  return status;
}

function normalizeReportExport(report) {
  if (!report) return null;
  return {
    id: report.id || "",
    type: report.type || "",
    title: report.title || "",
    periodStart: toIsoString(report.periodStart),
    periodEnd: toIsoString(report.periodEnd),
    createdAt: toIsoString(report.createdAt),
    metrics: report.metrics || {},
    milestoneProgress: Array.isArray(report.milestoneProgress) ? report.milestoneProgress : [],
    alerts: Array.isArray(report.alerts) ? report.alerts : [],
    aiSummary: report.aiSummary || null,
    recommendations: Array.isArray(report.recommendations) ? report.recommendations : [],
  };
}

export default function PilotOperationsSection({ user, orgId }) {
  const canManagePilotOps = user?.role === "admin" || user?.role === "superadmin";
  const [pilotConfig, setPilotConfig] = useState(null);
  const [pilotReports, setPilotReports] = useState([]);
  const [pilotOpsLoading, setPilotOpsLoading] = useState(false);
  const [pilotOpsError, setPilotOpsError] = useState("");
  const [pilotOpsNotice, setPilotOpsNotice] = useState("");
  const [pilotReportType, setPilotReportType] = useState("daily");
  const [pilotReportBusy, setPilotReportBusy] = useState(false);
  const [pilotInsights, setPilotInsights] = useState(null);
  const [pilotInsightsLoading, setPilotInsightsLoading] = useState(false);
  const [pilotInsightsError, setPilotInsightsError] = useState("");
  const [copyHumanSummaryStatus, setCopyHumanSummaryStatus] = useState("");
  const [sendReportEmail, setSendReportEmail] = useState(true);
  const [backfillDate, setBackfillDate] = useState("");
  const [backfillStart, setBackfillStart] = useState("");
  const [backfillEnd, setBackfillEnd] = useState("");
  const [rowActionBusyId, setRowActionBusyId] = useState("");

  const loadPilotOperations = useCallback(async () => {
    if (!user?.uid || !orgId || !canManagePilotOps) return;
    setPilotOpsLoading(true);
    setPilotOpsError("");
    try {
      const payload = await fetchPilotOperationsState(user, orgId, { max: 12 });
      setPilotConfig(payload.config || null);
      setPilotReports(Array.isArray(payload.reports) ? payload.reports : []);
    } catch (error) {
      setPilotOpsError(String(error?.message || "Unable to load pilot reports right now."));
    } finally {
      setPilotOpsLoading(false);
    }
  }, [canManagePilotOps, orgId, user]);

  useEffect(() => {
    loadPilotOperations().catch(() => {});
  }, [loadPilotOperations]);

  const latestPilotReport = pilotReports[0] || null;
  const latestReportSummary = latestPilotReport?.aiSummary || null;
  const targetProgressRows = Array.isArray(latestPilotReport?.milestoneProgress)
    ? latestPilotReport.milestoneProgress
    : [];
  const attentionFlags = Array.isArray(latestPilotReport?.alerts) ? latestPilotReport.alerts : [];

  const mappedInsightMetrics = useMemo(() => {
    const metrics = latestPilotReport?.metrics || {};
    return {
      totalSessions: metrics.totalSessions || 0,
      activeUsersLast7Days: metrics.activeUsers7d || 0,
      avgMessagesPerSession: metrics.avgMessagesPerSession || 0,
      followUpRate: metrics.followUpRate || 0,
      sessionsPerDayLast7Days: Array.isArray(metrics.sessionsPerDayLast7Days) ? metrics.sessionsPerDayLast7Days : [],
      returningUsers: metrics.returningUsers || 0,
      flaggedRate: metrics.flaggedRate || 0,
      flaggedSessionsCount: metrics.flaggedSessions || 0,
      followUpsCreated: metrics.followUpsCreated || 0,
      followUpsCompleted: metrics.followUpsCompleted || 0,
      sessionsWithoutFollowUp: metrics.sessionsWithoutFollowUp || 0,
    };
  }, [latestPilotReport]);

  const hasLatestPilotMetrics = useMemo(() => {
    if (!latestPilotReport) return false;
    const metrics = latestPilotReport.metrics || null;
    return Boolean(metrics && typeof metrics === "object" && Object.keys(metrics).length);
  }, [latestPilotReport]);

  const reportButtonDisabledReason = useMemo(() => {
    if (!canManagePilotOps) return "Only admin and superadmin users can run pilot reports.";
    if (!user?.uid) return "Sign in again to generate a pilot report.";
    if (!orgId) return "Select an organisation to generate a pilot report.";
    if (pilotReportBusy) return "Pilot report generation is already running.";
    return "";
  }, [canManagePilotOps, orgId, pilotReportBusy, user?.uid]);

  const backfillButtonDisabledReason = useMemo(() => {
    if (!canManagePilotOps) return "Only admin and superadmin users can backfill pilot reports.";
    if (!user?.uid) return "Sign in again to backfill a pilot report.";
    if (!orgId) return "Select an organisation to backfill a pilot report.";
    if (pilotReportBusy) return "Pilot report generation is already running.";
    if (!backfillDate && !(backfillStart && backfillEnd)) {
      return "Choose a report date or an exact start/end window.";
    }
    if ((backfillStart && !backfillEnd) || (!backfillStart && backfillEnd)) {
      return "Provide both exact start and end times.";
    }
    return "";
  }, [backfillDate, backfillEnd, backfillStart, canManagePilotOps, orgId, pilotReportBusy, user?.uid]);

  const insightsButtonDisabledReason = useMemo(() => {
    if (pilotInsightsLoading) return "Pilot insights generation is already running.";
    if (pilotOpsLoading && !latestPilotReport) return "Loading the latest pilot report.";
    if (!latestPilotReport) return "Generate a pilot report first to unlock AI insights.";
    if (!hasLatestPilotMetrics) return "The latest report is missing stored metrics. Generate a fresh pilot report.";
    return "";
  }, [hasLatestPilotMetrics, latestPilotReport, pilotInsightsLoading, pilotOpsLoading]);

  const handleGeneratePilotReport = useCallback(async () => {
    if (!user?.uid || !orgId || !canManagePilotOps) return;
    setPilotReportBusy(true);
    setPilotOpsError("");
    setPilotOpsNotice("");
    try {
      const result = await generatePilotReportNow(user, orgId, pilotReportType, {
        sendEmail: sendReportEmail,
      });
      if (!result?.ok && !result?.report) {
        throw new Error(result?.reason || "Unable to generate report right now.");
      }
      await loadPilotOperations();
    } catch (error) {
      setPilotOpsError(String(error?.message || "Unable to generate report right now."));
    } finally {
      setPilotReportBusy(false);
    }
  }, [canManagePilotOps, loadPilotOperations, orgId, pilotReportType, sendReportEmail, user]);

  const handleBackfillPilotReport = useCallback(async () => {
    if (!user?.uid || !orgId || !canManagePilotOps) return;
    if (backfillButtonDisabledReason) return;
    setPilotReportBusy(true);
    setPilotOpsError("");
    setPilotOpsNotice("");
    try {
      const periodStart = backfillStart ? new Date(backfillStart).getTime() : 0;
      const periodEnd = backfillEnd ? new Date(backfillEnd).getTime() : 0;
      const result = await generatePilotReportNow(user, orgId, pilotReportType, {
        backfill: true,
        date: backfillDate || "",
        periodStart,
        periodEnd,
        sendEmail: sendReportEmail,
      });
      if (!result?.ok && !result?.report) {
        throw new Error(result?.reason || "Unable to backfill report right now.");
      }
      await loadPilotOperations();
    } catch (error) {
      setPilotOpsError(String(error?.message || "Unable to backfill report right now."));
    } finally {
      setPilotReportBusy(false);
    }
  }, [
    backfillButtonDisabledReason,
    backfillDate,
    backfillEnd,
    backfillStart,
    canManagePilotOps,
    loadPilotOperations,
    orgId,
    pilotReportType,
    sendReportEmail,
    user,
  ]);

  const generatePilotInsights = useCallback(async () => {
    if (!latestPilotReport?.metrics) {
      setPilotInsightsError("Generate a pilot report first to create AI insights from stored metrics.");
      return;
    }
    setPilotInsightsLoading(true);
    setPilotInsightsError("");
    try {
      const response = await fetch("/api/pilot-insights", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          metrics: mappedInsightMetrics,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `API ${response.status}`);
      }
      setPilotInsights(payload?.insights || null);
    } catch (error) {
      setPilotInsightsError(String(error?.message || "Unable to generate insights right now."));
    } finally {
      setPilotInsightsLoading(false);
    }
  }, [latestPilotReport, mappedInsightMetrics]);

  const copyHumanSummaryDisabledReason = useMemo(() => {
    if (!latestPilotReport) return "Generate a pilot report first to copy a human-readable summary.";
    return "";
  }, [latestPilotReport]);

  const handleCopyHumanSummary = useCallback(async () => {
    if (!latestPilotReport) return;
    const summary = latestPilotReport.aiSummary || {};
    const metrics = latestPilotReport.metrics || {};
    const alertLines = Array.isArray(latestPilotReport.alerts) && latestPilotReport.alerts.length
      ? latestPilotReport.alerts.map((alert) => alert.label || alert.key || "Alert").join(", ")
      : "None";
    const recommendedActions = Array.isArray(latestPilotReport.recommendations) && latestPilotReport.recommendations.length
      ? latestPilotReport.recommendations
      : (Array.isArray(summary.recommendedActions) ? summary.recommendedActions : []);
    const humanSummary = [
      `Pilot Report — ${formatDateTime(latestPilotReport.createdAt)}`,
      "",
      "Pilot window:",
      `${formatDateTime(latestPilotReport.periodStart)} to ${formatDateTime(latestPilotReport.periodEnd)}`,
      "",
      "Summary:",
      [
        summary.pilotHealth,
        summary.engagementPatterns,
        summary.riskObservations,
        summary.followUpObservations,
      ].filter(Boolean).join(" "),
      "",
      "Engagement:",
      `- Active users: ${metrics.activeUsers7d ?? 0}`,
      `- Sessions per active user: ${metrics.sessionsPerActiveUser ?? 0}`,
      `- Avg messages per session: ${metrics.avgMessagesPerSession ?? 0}`,
      `- Returning users within 3 days: ${metrics.returningUsersWithin3Days ?? 0}`,
      "",
      "Follow-up:",
      `- Follow-ups created: ${metrics.followUpsCreated ?? 0}`,
      `- Follow-ups completed: ${metrics.followUpsCompleted ?? 0}`,
      `- Sessions without follow-up: ${metrics.sessionsWithoutFollowUp ?? 0}`,
      "",
      "Risk:",
      `- Unresolved critical issues: ${metrics.unresolvedCriticalIssues ?? 0}`,
      `- Alerts: ${alertLines}`,
      "",
      "Recommended actions:",
      ...(recommendedActions.length ? recommendedActions.map((item) => `- ${item}`) : ["- None recorded"]),
    ].join("\n");
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(humanSummary);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = humanSummary;
        textarea.setAttribute("readonly", "readonly");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopyHumanSummaryStatus("Human summary copied to clipboard.");
      window.setTimeout(() => setCopyHumanSummaryStatus(""), 2200);
    } catch {
      setCopyHumanSummaryStatus("Unable to copy the human summary right now.");
      window.setTimeout(() => setCopyHumanSummaryStatus(""), 2200);
    }
  }, [latestPilotReport]);

  const exportLatestReport = useCallback(() => {
    if (!latestPilotReport) return;
    const payload = normalizeReportExport(latestPilotReport);
    const stamp = toIsoString(latestPilotReport.createdAt).replace(/[:.]/g, "-") || "latest";
    downloadFile(
      `nia-pilot-report-${latestPilotReport.type || "latest"}-${stamp}.json`,
      JSON.stringify(payload, null, 2),
      "application/json"
    );
  }, [latestPilotReport]);

  const exportReportHistory = useCallback(() => {
    if (!pilotReports.length) return;
    const header = [
      "id",
      "type",
      "title",
      "periodStart",
      "periodEnd",
      "createdAt",
      "metrics",
      "milestoneProgress",
      "alerts",
      "aiSummary",
      "recommendations",
    ];
    const rows = pilotReports.map((report) => {
      const normalized = normalizeReportExport(report);
      return [
        normalized.id,
        normalized.type,
        normalized.title,
        normalized.periodStart,
        normalized.periodEnd,
        normalized.createdAt,
        JSON.stringify(normalized.metrics),
        JSON.stringify(normalized.milestoneProgress),
        JSON.stringify(normalized.alerts),
        JSON.stringify(normalized.aiSummary),
        JSON.stringify(normalized.recommendations),
      ];
    });
    const csv = [header, ...rows].map((row) => row.map(toCsvValue).join(",")).join("\n");
    downloadFile("nia-pilot-report-history.csv", csv, "text/csv;charset=utf-8");
  }, [pilotReports]);

  const handleResendReport = useCallback(async (report) => {
    if (!user?.uid || !orgId || !canManagePilotOps || !report?.id) return;
    setRowActionBusyId(report.id);
    setPilotOpsError("");
    setPilotOpsNotice("");
    try {
      const result = await resendPilotReportEmail(user, report.orgId || orgId, report.id);
      if (!result?.ok) {
        throw new Error(result?.reason || "Unable to resend report email right now.");
      }
      setPilotOpsNotice("Report email resent successfully.");
      await loadPilotOperations();
    } catch (error) {
      setPilotOpsError(String(error?.message || "Unable to resend report email right now."));
    } finally {
      setRowActionBusyId("");
    }
  }, [canManagePilotOps, loadPilotOperations, orgId, user]);

  const renderDownloadActions = useCallback((report) => {
    const links = [
      report?.jsonDownloadUrl ? { href: report.jsonDownloadUrl, label: "JSON" } : null,
      report?.csvDownloadUrl ? { href: report.csvDownloadUrl, label: "CSV" } : null,
      report?.summaryDownloadUrl ? { href: report.summaryDownloadUrl, label: "Summary" } : null,
    ].filter(Boolean);
    if (!links.length) return "—";
    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {links.map((link) => (
          <a
            key={`${report.id}-${link.label}`}
            className="btn btn-ghost"
            href={link.href}
            target="_blank"
            rel="noreferrer"
            style={{ padding: "6px 10px" }}
          >
            {link.label}
          </a>
        ))}
      </div>
    );
  }, []);

  const renderRowActions = useCallback((report) => (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => handleResendReport(report)}
        disabled={Boolean(rowActionBusyId) || !report?.periodStart || !report?.periodEnd}
        style={{ padding: "6px 10px" }}
      >
        {rowActionBusyId === report?.id ? "Resending..." : "Resend"}
      </button>
    </div>
  ), [handleResendReport, rowActionBusyId]);

  if (!canManagePilotOps) return null;

  return (
    <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
      <Card title="Pilot Operations & AI Insights">
        <div style={{ display: "grid", gap: 14 }}>
          <div className="dashboard-section-head">
            <div>
              <div className="nia-subtitle">Pilot reporting</div>
              <div className="nia-muted">Stored operational summaries, target progress, and attention flags for the live pilot.</div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div className="nia-muted">
              Latest automated pilot summary, target progress, and stored report history.
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select
                className="input"
                value={pilotReportType}
                onChange={(event) => setPilotReportType(event.target.value)}
                style={{ minWidth: 170 }}
              >
                <option value="morning">Morning summary</option>
                <option value="evening">Evening summary</option>
                <option value="daily">Daily summary</option>
                <option value="weekly">Weekly report</option>
              </select>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleGeneratePilotReport}
                disabled={Boolean(reportButtonDisabledReason)}
              >
                {pilotReportBusy ? "Generating..." : "Generate Report Now"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={exportLatestReport}
                disabled={!latestPilotReport}
              >
                Export Latest Report (.json)
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={exportReportHistory}
                disabled={!pilotReports.length}
              >
                Export Report History (.csv)
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCopyHumanSummary}
                disabled={Boolean(copyHumanSummaryDisabledReason)}
              >
                Copy Human Summary
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <label className="nia-muted" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={sendReportEmail}
                onChange={(event) => setSendReportEmail(event.target.checked)}
              />
              Email superadmin after generation
            </label>
            <span className="nia-muted">Scheduled morning and evening runs attempt email delivery when recipients are available.</span>
          </div>

          {reportButtonDisabledReason ? <div className="nia-muted">{reportButtonDisabledReason}</div> : null}
          {pilotReportType === "daily" ? (
            <div className="nia-muted">Daily reports use the latest completed local day unless you use Backfill.</div>
          ) : null}
          {!latestPilotReport ? <div className="nia-muted">Generate a pilot report before exporting the latest JSON snapshot.</div> : null}
          {!pilotReports.length ? <div className="nia-muted">Pilot report history export becomes available after reports are loaded.</div> : null}
          {copyHumanSummaryDisabledReason ? <div className="nia-muted">{copyHumanSummaryDisabledReason}</div> : null}
          {copyHumanSummaryStatus ? <div className="nia-muted">{copyHumanSummaryStatus}</div> : null}

          {pilotOpsError ? <div className="nia-muted">{pilotOpsError}</div> : null}
          {pilotOpsNotice ? <div className="nia-muted">{pilotOpsNotice}</div> : null}

          <Card title="Backfill">
            <div style={{ display: "grid", gap: 12 }}>
              <div className="nia-muted">
                Regenerate a missed morning, evening, daily, or weekly report for a specific date, or provide an exact start/end window.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
                <label style={{ display: "grid", gap: 4 }}>
                  <span className="nia-muted">Date</span>
                  <input className="input" type="date" value={backfillDate} onChange={(event) => setBackfillDate(event.target.value)} />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span className="nia-muted">Exact start</span>
                  <input className="input" type="datetime-local" value={backfillStart} onChange={(event) => setBackfillStart(event.target.value)} />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span className="nia-muted">Exact end</span>
                  <input className="input" type="datetime-local" value={backfillEnd} onChange={(event) => setBackfillEnd(event.target.value)} />
                </label>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={handleBackfillPilotReport}
                  disabled={Boolean(backfillButtonDisabledReason)}
                >
                  {pilotReportBusy ? "Running..." : "Backfill Report"}
                </button>
              </div>
              {backfillButtonDisabledReason ? <div className="nia-muted">{backfillButtonDisabledReason}</div> : null}
            </div>
          </Card>

          <div className="nia-grid nia-grid--3">
            <Stat label="Pilot Window" value={pilotConfig ? `${pilotConfig.startDate} to ${pilotConfig.endDate}` : "—"} subtle={pilotConfig ? `${pilotConfig.durationWeeks} weeks` : "Config pending"} />
            <Stat label="Latest Report" value={latestPilotReport ? latestPilotReport.title : "None"} subtle={latestPilotReport ? formatDateTime(latestPilotReport.createdAt) : "No stored reports yet"} />
            <Stat
              label="Email Delivery"
              value={latestPilotReport?.emailStatus || latestPilotReport?.delivery?.status || "Pending"}
              subtle={
                latestPilotReport?.emailStatus === "accepted"
                  ? "Accepted by relay; no provider delivery confirmation recorded."
                  : latestPilotReport?.emailStatus === "unknown"
                    ? "Attempted, but delivery status is unconfirmed."
                    : (latestPilotReport?.emailSentAt ? formatDateTime(latestPilotReport.emailSentAt) : "No confirmed provider delivery recorded yet")
              }
            />
          </div>

          {attentionFlags.length ? (
            <div className="nia-list">
              {attentionFlags.map((alert) => (
                <div key={alert.key} className="nia-list-btn" style={{ cursor: "default" }}>
                  <span>{alert.label}</span>
                  <span className="nia-pill">{String(alert.severity || "info").toUpperCase()}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="nia-muted">
              {pilotOpsLoading ? "Loading pilot operations..." : "No attention flags on the latest stored report."}
            </div>
          )}

          {latestReportSummary ? (
            <div style={{ display: "grid", gap: 10 }}>
              <div>
                <strong>Latest AI report summary</strong>
                <div className="nia-muted" style={{ marginTop: 4 }}>{latestReportSummary.pilotHealth}</div>
              </div>
              <div>
                <strong>Engagement</strong>
                <div className="nia-muted" style={{ marginTop: 4 }}>{latestReportSummary.engagementPatterns}</div>
              </div>
              <div>
                <strong>Risk</strong>
                <div className="nia-muted" style={{ marginTop: 4 }}>{latestReportSummary.riskObservations}</div>
              </div>
              <div>
                <strong>Follow-up</strong>
                <div className="nia-muted" style={{ marginTop: 4 }}>{latestReportSummary.followUpObservations}</div>
              </div>
            </div>
          ) : (
            <div className="nia-muted">
              Generate a pilot report to store an operational snapshot with AI summary and recommendations.
            </div>
          )}

          <div className="nia-grid nia-grid--2">
            <Card title="Target Progress">
              <Table
                headers={["Target", "Current", "Goal", "Status"]}
                rows={
                  targetProgressRows.length
                    ? targetProgressRows.map((item) => [
                        item.label,
                        `${item.current}${item.unit ? ` ${item.unit}` : ""}`,
                        `${item.target}${item.unit ? ` ${item.unit}` : ""}`,
                        <span className="nia-pill">{item.met ? "On Track" : "Watch"}</span>,
                      ])
                    : [["-", "-", "-", pilotOpsLoading ? "Loading..." : "No report yet"]]
                }
              />
            </Card>

            <Card title="Report History">
              <Table
                headers={["Type", "Period", "Created", "Email", "Download", "Resend"]}
                rows={
                  pilotReports.length
                    ? pilotReports.map((report) => [
                        report.title,
                        `${formatDateTime(report.periodStart)} to ${formatDateTime(report.periodEnd)}`,
                        formatDateTime(report.createdAt),
                        formatEmailStatus(report),
                        renderDownloadActions(report),
                        renderRowActions(report),
                      ])
                    : [["-", "-", "-", "-", "-", pilotOpsLoading ? "Loading..." : "No pilot reports yet"]]
                }
              />
            </Card>
          </div>
        </div>
      </Card>

      <Card title="AI Pilot Insights">
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div className="nia-muted">Concise operational summary generated from the latest stored pilot metrics.</div>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={generatePilotInsights}
              disabled={Boolean(insightsButtonDisabledReason)}
            >
              {pilotInsightsLoading ? "Generating..." : "Generate Insights"}
            </button>
          </div>
          {insightsButtonDisabledReason ? <div className="nia-muted">{insightsButtonDisabledReason}</div> : null}
          {pilotInsightsError ? <div className="nia-muted">{pilotInsightsError}</div> : null}
          {pilotInsights ? (
            <div style={{ display: "grid", gap: 10 }}>
              <div>
                <strong>Pilot health</strong>
                <div className="nia-muted" style={{ marginTop: 4 }}>{pilotInsights.pilotHealth}</div>
              </div>
              <div>
                <strong>Engagement</strong>
                <div className="nia-muted" style={{ marginTop: 4 }}>{pilotInsights.engagementPatterns}</div>
              </div>
              <div>
                <strong>Risk</strong>
                <div className="nia-muted" style={{ marginTop: 4 }}>{pilotInsights.riskObservations}</div>
              </div>
              <div>
                <strong>Follow-up</strong>
                <div className="nia-muted" style={{ marginTop: 4 }}>{pilotInsights.followUpObservations}</div>
              </div>
              <div>
                <strong>Recommended actions</strong>
                <div className="nia-list" style={{ marginTop: 8 }}>
                  {pilotInsights.recommendedActions.map((item, index) => (
                    <div key={`${index}-${item}`} className="nia-list-btn" style={{ cursor: "default" }}>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="nia-muted">Generate insights to review pilot health, engagement, risk, and recommended next actions.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
